import { useState, useCallback, useEffect, useRef } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

const POLL_INTERVAL = 2 * 60 * 1000; // 2 min polling

export function usePWAUpdate() {
  const [dismissed, setDismissed] = useState(false);
  const registrationRef = useRef<ServiceWorkerRegistration | undefined>(undefined);

  const {
    needRefresh: [needRefreshVal, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      registrationRef.current = registration;

      // Catch updates that arrived before the hook mounted
      if (registration.waiting) {
        setNeedRefresh(true);
      }

      // Proactive polling every 2 minutes
      setInterval(() => {
        registration.update().catch(() => {});
      }, POLL_INTERVAL);
    },
    onRegisterError(error) {
      console.error('SW registration error:', error);
    },
  });

  // Re-check on visibility / focus / online
  useEffect(() => {
    const check = () => {
      registrationRef.current?.update().catch(() => {});
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        setDismissed(false);
        check();
      }
    };
    const onFocus = () => {
      setDismissed(false);
      check();
    };

    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);
    window.addEventListener('online', check);

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('online', check);
    };
  }, []);

  // Reset dismissed when a new update arrives
  useEffect(() => {
    if (needRefreshVal) {
      setDismissed(false);
    }
  }, [needRefreshVal]);

  const applyUpdate = useCallback(() => {
    updateServiceWorker(true);
  }, [updateServiceWorker]);

  const dismiss = useCallback(() => {
    setDismissed(true);
    setNeedRefresh(false);
  }, [setNeedRefresh]);

  return {
    needRefresh: needRefreshVal && !dismissed,
    applyUpdate,
    dismiss,
  };
}
